import {
  ContainerCall,
  ErrorOptions,
} from 'features/method-tester/types/SDKCallResult';
import { useState } from 'react';

import { useEnvironmentModeContext } from '../../../context/EnvironmentModeContext';
import { useIntegrationWarning } from '../../../context/IntegrationWarningContext';
import { useMiniAppSDK } from '../../../context/SDKConfigContext';
declare global {
  interface Window {
    gpContainer: {
      call: ContainerCall;
    };
  }
}

export const useSDKHandler = <T>() => {
  const [response, setResponse] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showWarning } = useIntegrationWarning();
  const { isDevMode } = useEnvironmentModeContext();
  const sdk = useMiniAppSDK();

  const handleError = (err: unknown, includeDetails = false) => {
    const is1001 = (err as Error)?.message?.includes('1001');
    const code = is1001 ? 1001 : 500;
    const message = is1001
      ? 'Service Unavailable'
      : 'Unexpected error occurred';

    if (is1001) {
      showWarning();
    }

    const errorResult = !includeDetails
      ? (err as T)
      : ({
          success: false,
          error: {
            code,
            message,
          },
        } as T);

    setResponse(errorResult);
  };

  const withLoadingState = async (operation: () => Promise<void>) => {
    setIsLoading(true);
    try {
      await operation();
    } finally {
      setIsLoading(false);
    }
  };

  const invokeSDKActionV0_3 = async (
    callSDKOperation: () => Promise<T>,
    options: ErrorOptions
  ) => {
    await withLoadingState(async () => {
      try {
        await sdk.setEnvironment({
          useDummyService: isDevMode,
          options,
        });
        const response = await callSDKOperation();
        setResponse(response);
      } catch (err: unknown) {
        handleError(err);
      }
    });
  };

  const invokeSDKActionV1 = async (
    className: string,
    methodName: string,
    payload: Record<string, unknown>
  ) => {
    await withLoadingState(async () => {
      try {
        window.gpContainer.call(
          className,
          methodName,
          payload,
          (res: unknown) => {
            console.log('Success callback:', res);
            setResponse(res as T);
          },
          (err: unknown) => {
            console.error('Error callback:', err);
            handleError(err);
          }
        );
      } catch (e) {
        handleError(e, true);
      }
    });
  };

  const clearResponse = () => {
    setResponse(null);
  };

  return {
    isLoading,
    response,
    invokeSDKActionV0_3,
    invokeSDKActionV1,
    clearResponse,
  };
};
